import React, { Component } from 'react'
import styled from 'styled-components'
import { get } from 'lodash'
import Shell from '../EditorTools/Shell'
import Button from '../../../../components/Button'
import ErrorBoundary from '../../../../components/ErrorBoundary'
import { wpAxios } from '../../../../config/axiosResolver'
import ConfigResolver from '../../../../config/ConfigResolver'

const config = ConfigResolver.config()

// As in History: standalone it sizes itself, embedded it fills the workspace
// panel it has been placed in rather than declaring a second width inside it.
const Panel = styled.div`
  display: flex;
  flex-direction: column;

  ${p => p.$embedded
    ? `
      flex: 1;
      min-height: 0;
      padding: 18px 30px 22px;
    `
    : `
      width: 760px;
      max-width: 92vw;
      max-height: 88vh;
      padding: 30px 34px 24px;
    `}
`

const Title = styled.div`
  font-family: Hind;
  font-size: 22px;
  color: #000;
`

const Sub = styled.div`
  font-family: Hind;
  font-size: 14px;
  font-weight: 300;
  color: #9F9E9B;
  margin: 4px 0 18px;
`

/* The draft itself. A plain textarea rather than the rich editor used for the
   prompt fields: the opening is one paragraph of running prose, and the vendor
   links are attached by the server on save, so there is no markup for an editor
   to manage here. */
const Draft = styled.textarea`
  font-family: Hind;
  font-size: 16px;
  line-height: 1.6;
  color: #404040;
  width: 100%;
  /* Takes the height left over once the re-ask box below it has what it needs,
     rather than staying at its minimum and leaving the panel half empty. */
  flex: 1;
  min-height: 190px;
  box-sizing: border-box;
  padding: 14px 16px;
  border: 1px solid #E4E0DC;
  border-radius: 3px;
  resize: vertical;
  outline: none;

  &:focus { border-color: #ECB595; }
  &:disabled { background: #FAFAFA; color: #9F9E9B; }
`

const Meta = styled.div`
  font-family: Hind;
  font-size: 12px;
  color: #9F9E9B;
  margin: 6px 2px 20px;
  display: flex;
  justify-content: space-between;
`

const AskLabel = styled.div`
  font-family: Hind;
  font-size: 14px;
  color: #55575A;
  margin-bottom: 6px;
`

const Ask = styled.textarea`
  font-family: Hind;
  font-size: 15px;
  line-height: 1.5;
  color: #404040;
  width: 100%;
  min-height: 62px;
  box-sizing: border-box;
  padding: 11px 14px;
  border: 1px solid #E4E0DC;
  border-radius: 3px;
  resize: vertical;
  outline: none;

  &::placeholder { color: #C4C0BC; }
  &:focus { border-color: #ECB595; }
  &:disabled { background: #FAFAFA; }
`

/* Takes the space the header and footer do not, rather than sizing to its own
   content. Without this the footer sat wherever the draft happened to end and
   left a band of empty panel beneath it, and the empty-state overlay -- which
   is positioned against this box -- centred itself against the text rather
   than against the tab. */
const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-right: 4px;
  position: relative;
`

/* The empty state sits ON the panel rather than replacing it, so an editor can
   see what they are about to get before they spend a model call on it. */
const Fadeable = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  opacity: ${p => p.dim ? 0.28 : 1};
  pointer-events: ${p => p.dim ? 'none' : 'auto'};
  user-select: ${p => p.dim ? 'none' : 'auto'};
`

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 40px;
`

const OverlayTitle = styled.div`
  font-family: Hind;
  font-size: 17px;
  color: #404040;
  margin-bottom: 6px;
`

const OverlayText = styled.div`
  font-family: Hind;
  font-size: 14px;
  font-weight: 300;
  color: #55575A;
  line-height: 1.6;
  max-width: 400px;
  margin-bottom: 20px;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 18px;
  margin-top: 4px;
  border-top: 1px solid #EDEAE8;
`

const Spacer = styled.div`flex: 1;`

const Cancel = styled.div`
  font-family: Hind;
  font-size: 15px;
  font-weight: 300;
  color: #ECB595;
  cursor: pointer;
`

const Notice = styled.div`
  font-family: Hind;
  font-size: 13px;
  font-weight: 300;
  color: ${p => p.tone === 'bad' ? '#B4776F' : '#777'};
  background: #FAFAFA;
  border-left: 2px solid ${p => p.tone === 'bad' ? '#B4776F' : '#ECB595'};
  padding: 9px 12px;
  margin-bottom: 16px;
`

/**
 * STY-582: review, edit and re-ask for a drafted opening paragraph.
 *
 * The first version of this shipped as a read-only diff with Accept and
 * Decline, which made the draft take-it-or-leave-it: an editor who wanted one
 * sentence changed had to reject the whole thing and hope the next run came
 * back different. Here the draft is a starting point — editable directly, or
 * handed back with a note.
 *
 * Nothing is written until Save. Drafting and re-drafting only return text.
 */
class OpeningDraft extends Component {
  constructor (props) {
    super(props)

    // An opening already on the document loads straight in; the vendor links
    // are stripped back to plain text because that is what the editor edits and
    // the server reattaches them on save.
    // Block tags are word boundaries; inline tags are not. Replacing every tag
    // with a space put one where the markup had none, so a linked vendor
    // followed by a comma came back as "Vendor ," and "<a>Smith & Co.</a>'s"
    // as "Smith & Co. 's" -- and Save would then persist that.
    const existing = (props.existingIntro || '')
      .replace(/<\s*(br|p|div|li|h[1-6])\b[^>]*>/gi, ' ')
      .replace(/<\s*\/\s*(p|div|li|h[1-6])\s*>/gi, ' ')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim()

    this.state = {
      text: existing,
      hasExisting: !!existing,
      ask: '',
      baseHash: null,
      // The last text the MODEL produced, which is what a re-ask reacts to.
      // Kept apart from `text` so an editor's own wording is not fed back as
      // though the model had written it.
      lastGenerated: existing,
      loading: false,
      saving: false,
      notice: null,
      tone: 'info',
      dirty: false
    }
  }

  // Nothing runs on arrival. Clicking a tab is not the same as asking for a
  // paragraph to be written, and a model call starting on its own — with a
  // seven second wait attached — is a surprise, not a feature. Where an opening
  // already exists it is simply loaded; where it does not, the empty state asks.

  componentDidUpdate (prevProps, prevState) {
    // Embedded, the modal belongs to the workspace, so guarding our own
    // handleClose does nothing -- Shell returns bare children and the backdrop
    // and Escape stay live over an in-flight save. The owner has to know.
    const was = prevState.loading || prevState.saving
    const now = this.state.loading || this.state.saving

    if (was !== now && this.props.onBusy) this.props.onBusy(now)
  }

  endpoint () {
    return `${config.siteUrl}/api/v2/submissions/${this.props.submissionId}/ai-draft-intro`
  }

  generate () {
    const { text, lastGenerated } = this.state
    const { flushSaves } = this.props
    const ask = this.state.ask.trim()

    this.setState({ loading: true, notice: null })

    // Only send the previous draft when there is a note to react to. Without
    // one this is a fresh attempt, and handing back the old text would have it
    // politely rewrite what the editor has just rejected.
    const payload = ask
      ? { previous: lastGenerated || text, instructions: ask }
      : {}

    // The server drafts from the STORED submission, so a save still queued
    // from the field the editor just left would be written from text the
    // opening was not based on -- and land after it, taking the base hash with
    // it. The copy tab already waits for this; this one was posting straight
    // past it.
    const ready = flushSaves ? flushSaves() : Promise.resolve()

    ready.then(
      () => wpAxios.post(this.endpoint(), payload)
      .then((res) => {
        const data = get(res, 'data.data', {})

        if (!data.text) {
          this.setState({
            loading: false,
            tone: 'bad',
            notice: data.rejected
              ? `That attempt ${data.rejected}, so nothing was changed. Try asking for something more specific, or edit the text yourself.`
              : 'No opening was produced.'
          })

          return
        }

        this.setState({
          loading: false,
          text: data.text,
          lastGenerated: data.text,
          baseHash: data.baseHash || this.state.baseHash,
          ask: '',
          dirty: false,
          notice: ask ? 'Rewritten. The previous version is gone once you close this.' : null,
          tone: 'info'
        })
      })
      .catch((err) => {
        this.setState({
          loading: false,
          tone: 'bad',
          notice: get(err, 'response.data.message')
            || 'Could not reach the opening writer. Nothing was changed.'
        })
      }),
      () => {
        this.setState({
          loading: false,
          tone: 'bad',
          notice: 'Your last edit has not been saved yet, so nothing was drafted. Save that change first.'
        })
      }
    )
  }

  save () {
    const { onSave, handleClose, flushSaves } = this.props
    const text = this.state.text.trim()

    if (!text || this.state.saving) return

    this.setState({ saving: true, notice: null })

    // Same reason drafting waits, and the consequence is sharper here: the
    // link response carries the base hash, so a field save landing after it
    // moves the document out from under the opening and the write is refused
    // as stale. Fixing generate() and not this was half a fix.
    const ready = flushSaves ? flushSaves() : Promise.resolve()

    ready.then(
      // The server attaches the vendor links, because it owns the credit list
      // and the profile slugs. Doing it here would mean inventing URLs in the
      // client.
      () => wpAxios.post(this.endpoint(), { action: 'link', text: text })
      .then((res) => {
        const data = get(res, 'data.data', {})

        // The GENERATION hash, not the link response's. Linking is a pure text
        // transform that reports the document as it stands at that moment, so
        // if somebody else edited the submission between drafting and saving,
        // preferring the fresher hash tells the server this write is based on
        // their version. It is not: the payload is this client's snapshot from
        // before their edit, so the check passes and their work is overwritten
        // instead of the save being refused. Pinning to the hash we drafted
        // against is what turns that into the 409 it should be.
        return Promise.resolve(onSave(data.html, this.state.baseHash || data.baseHash))
          .then(() => {
            this.setState({ saving: false })
            handleClose()
          })
      })
      .catch((err) => {
        this.setState({
          saving: false,
          tone: 'bad',
          notice: get(err, 'response.data.message')
            || 'The opening could not be saved. Nothing was changed.'
        })
      }),
      () => {
        this.setState({
          saving: false,
          tone: 'bad',
          notice: 'Your last edit has not been saved yet, so the opening was not saved. Save that change first.'
        })
      }
    )
  }

  render () {
    const { open, handleClose } = this.props
    const { text, ask, loading, saving, notice, tone, dirty } = this.state

    const words = text.trim() ? text.trim().split(/\s+/).length : 0
    const busy = loading || saving
    // Nothing written and nothing loaded: show the ask, and fade what is behind
    // it so the shape of the tool is still legible.
    const showEmpty = !text.trim() && !loading

    return (
      <ErrorBoundary>
        <Shell embedded={this.props.embedded} open={open} handleClose={busy ? () => {} : handleClose}>
          <Panel $embedded={this.props.embedded}>
            <Title>Opening paragraph</Title>
            <Sub>
              Edit it directly, or tell the writer what to change and ask again.
              Nothing is saved until you choose Save.
            </Sub>

            <Body>
              {notice && <Notice tone={tone}>{notice}</Notice>}

              {showEmpty && (
                <Overlay>
                  <OverlayTitle>No opening paragraph yet</OverlayTitle>
                  <OverlayText>
                    One paragraph in SMP's voice, written from what the couple
                    submitted and the vendors credited. You can edit it or ask
                    for changes before anything is saved.
                  </OverlayText>
                  <Button
                    text='Write an opening'
                    width={200}
                    showLoader={loading}
                    disabled={loading}
                    onClick={() => this.generate()}
                  />
                </Overlay>
              )}

              <Fadeable dim={showEmpty}>
                <Draft
                  value={loading ? 'Writing…' : text}
                  disabled={busy}
                  onChange={(e) => this.setState({ text: e.target.value, dirty: true })}
                  placeholder='The drafted opening will appear here.'
                />
                <Meta>
                  <span>{words} words{dirty ? ' · edited by you' : ''}</span>
                  <span>Vendor links are added when you save.</span>
                </Meta>

                <AskLabel>Ask for a change</AskLabel>
                <Ask
                  value={ask}
                  disabled={busy}
                  onChange={(e) => this.setState({ ask: e.target.value })}
                  placeholder={'e.g. Mention the truffle risotto and the fireworks. Drop the bit about the bridesmaids. Do not open with the venue name.'}
                />
              </Fadeable>
            </Body>

            <Footer>
              <Button
                text={ask.trim() ? 'Rewrite with my notes' : 'Try a different one'}
                width={200}
                showLoader={loading}
                disabled={busy}
                onClick={() => this.generate()}
              />
              <Spacer />
              <Cancel onClick={() => busy ? null : handleClose()}>Cancel</Cancel>
              <Button
                text='Save opening'
                width={150}
                showLoader={saving}
                disabled={busy || !text.trim()}
                onClick={() => this.save()}
              />
            </Footer>
          </Panel>
        </Shell>
      </ErrorBoundary>
    )
  }
}

export default OpeningDraft
